import * as React from 'react';
import { subDays } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import type { BreakdownTimeSeriesRequest } from '@/data/types/breakdown.types';
import type { TotalSpendRequest, TotalSpendMetadataFilter, ProvidersBreakdownRequest, ProvidersBreakdownMetadataFilter, ContributorsSpendRequest, ContributorsSpendMetadataFilter, SpendRequest, SpendMetadataFilter } from '@/data/types/dashboard.types';
import type { GranularityValue } from '@/components/GranularitySelector';
import type { GroupByValue } from '@/components/GroupBySelector';
import { useBreakdownTimeSeries } from '@/data/hooks/api/useBreakdown';
import { useTotalSpend, useProvidersBreakdown, useContributorsSpend, useSpend } from '@/data/hooks/api/useDashboards';
import type { ModelSelection } from '@/data/types/common.types';
import { ALL_MODELS_SELECTION } from '@/components/ModelsSelector';

// Default configuration for Overview/Custom Dashboards
const DEFAULT_DATE_RANGE: DateRange = {
  from: subDays(new Date(), 29),
  to: new Date(),
};
const DEFAULT_GRANULARITY: GranularityValue = 'daily';
const DEFAULT_GROUP_BY: GroupByValue = 'modality';

export function useOverviewTimeSeries(
  dateRange?: DateRange,
  modelSelection?: ModelSelection,
  metadataFilters?: Array<{ key: string; operator: 'eq' | 'neq'; value: string }> | null
) {
  // Use provided date range or fallback to default
  const effectiveDateRange = dateRange || DEFAULT_DATE_RANGE;
  
  // Default to all models if not provided
  const effectiveModelSelection = modelSelection || ALL_MODELS_SELECTION;

  // Build API request for timeseries
  const buildTimeSeriesRequest = React.useCallback((): BreakdownTimeSeriesRequest | null => {
    if (!effectiveDateRange?.from || !effectiveDateRange?.to) return null;

    const startDate = new Date(effectiveDateRange.from);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(effectiveDateRange.to);
    endDate.setHours(23, 59, 59, 999);

    const request: BreakdownTimeSeriesRequest = {
      date_from: startDate.toISOString(),
      date_to: endDate.toISOString(),
      granularity: DEFAULT_GRANULARITY,
      group_by: DEFAULT_GROUP_BY,
      model_selection: effectiveModelSelection,
      providers: null,
      metadata: metadataFilters && metadataFilters.length > 0 ? metadataFilters : null,
    };

    return request;
  }, [effectiveDateRange, effectiveModelSelection, metadataFilters]);

  const timeSeriesRequest = buildTimeSeriesRequest();
  
  const { data: timeSeriesResponse, isLoading: isLoadingTimeSeries } = useBreakdownTimeSeries(
    timeSeriesRequest,
    timeSeriesRequest !== null
  );

  return {
    timeSeriesData: timeSeriesResponse?.data || [],
    isLoading: isLoadingTimeSeries,
    dateRange: effectiveDateRange,
    granularity: DEFAULT_GRANULARITY,
    groupBy: DEFAULT_GROUP_BY,
  };
}

// Hook to map breakdown metadata filter format to total-spend format
function mapMetadataFilters(
  filters?: Array<{ key: string; operator: 'eq' | 'neq'; value: string }> | null
): TotalSpendMetadataFilter[] | null {
  if (!filters || filters.length === 0) return null;
  
  return filters.map((f) => ({
    key: f.key,
    operation: f.operator === 'eq' ? 'is' as const : 'is_not' as const,
    value: f.value,
  }));
}

export function useOverviewTotalSpend(
  dateRange?: DateRange,
  metadataFilters?: Array<{ key: string; operator: 'eq' | 'neq'; value: string }> | null
) {
  // Use provided date range or fallback to default
  const effectiveDateRange = dateRange || DEFAULT_DATE_RANGE;

  // Use the same date range as the timeseries
  const buildTotalSpendRequest = React.useCallback((): TotalSpendRequest | null => {
    if (!effectiveDateRange?.from || !effectiveDateRange?.to) return null;

    const startDate = new Date(effectiveDateRange.from);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(effectiveDateRange.to);
    endDate.setHours(23, 59, 59, 999);

    const request: TotalSpendRequest = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      model_selection: ALL_MODELS_SELECTION, // Include all models for overview
      providers: null, // Include all providers for overview
      metadata_filters: mapMetadataFilters(metadataFilters),
    };

    return request;
  }, [effectiveDateRange, metadataFilters]);

  const totalSpendRequest = buildTotalSpendRequest();

  const { data: totalSpendData, isLoading: isLoadingTotalSpend } = useTotalSpend(
    totalSpendRequest,
    totalSpendRequest !== null
  );

  return {
    totalSpendData,
    isLoadingTotalSpend,
  };
}

// Hook to map breakdown metadata filter format to providers breakdown format
function mapMetadataFiltersForProvidersBreakdown(
  filters?: Array<{ key: string; operator: 'eq' | 'neq'; value: string }> | null
): ProvidersBreakdownMetadataFilter[] | null {
  if (!filters || filters.length === 0) return null;
  
  return filters.map((f) => ({
    key: f.key,
    operation: f.operator === 'eq' ? 'is' as const : 'is_not' as const,
    value: f.value,
  }));
}

export function useOverviewProvidersBreakdown(
  dateRange?: DateRange,
  metadataFilters?: Array<{ key: string; operator: 'eq' | 'neq'; value: string }> | null
) {
  // Use provided date range or fallback to default
  const effectiveDateRange = dateRange || DEFAULT_DATE_RANGE;

  // Use the same date range as the timeseries
  const buildProvidersBreakdownRequest = React.useCallback((): ProvidersBreakdownRequest | null => {
    if (!effectiveDateRange?.from || !effectiveDateRange?.to) return null;

    const startDate = new Date(effectiveDateRange.from);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(effectiveDateRange.to);
    endDate.setHours(23, 59, 59, 999);

    const request: ProvidersBreakdownRequest = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      model_selection: ALL_MODELS_SELECTION, // Include all models for overview
      providers: null, // Include all providers for overview
      metadata_filters: mapMetadataFiltersForProvidersBreakdown(metadataFilters),
    };

    return request;
  }, [effectiveDateRange, metadataFilters]);

  const providersBreakdownRequest = buildProvidersBreakdownRequest();

  const { data: providersBreakdownData, isLoading: isLoadingProvidersBreakdown } = useProvidersBreakdown(
    providersBreakdownRequest,
    providersBreakdownRequest !== null
  );

  return {
    providersBreakdownData,
    isLoadingProvidersBreakdown,
  };
}

// Hook to map breakdown metadata filter format to contributors spend format
function mapMetadataFiltersForContributorsSpend(
  filters?: Array<{ key: string; operator: 'eq' | 'neq'; value: string }> | null
): ContributorsSpendMetadataFilter[] | null {
  if (!filters || filters.length === 0) return null;
  
  return filters.map((f) => ({
    key: f.key,
    operation: f.operator === 'eq' ? 'is' as const : 'is_not' as const,
    value: f.value,
  }));
}

export function useOverviewContributorsSpend(
  dateRange?: DateRange,
  metadataFilters?: Array<{ key: string; operator: 'eq' | 'neq'; value: string }> | null
) {
  // Use provided date range or fallback to default
  const effectiveDateRange = dateRange || DEFAULT_DATE_RANGE;

  // Use the same date range as the timeseries
  const buildContributorsSpendRequest = React.useCallback((): ContributorsSpendRequest | null => {
    if (!effectiveDateRange?.from || !effectiveDateRange?.to) return null;

    const startDate = new Date(effectiveDateRange.from);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(effectiveDateRange.to);
    endDate.setHours(23, 59, 59, 999);

    const request: ContributorsSpendRequest = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      metadata_filters: mapMetadataFiltersForContributorsSpend(metadataFilters),
    };

    return request;
  }, [effectiveDateRange, metadataFilters]);

  const contributorsSpendRequest = buildContributorsSpendRequest();

  const { data: contributorsSpendData, isLoading: isLoadingContributorsSpend } = useContributorsSpend(
    contributorsSpendRequest,
    contributorsSpendRequest !== null
  );

  return {
    contributorsSpendData,
    isLoadingContributorsSpend,
  };
}

// Hook to map breakdown metadata filter format to spend format
function mapMetadataFiltersForSpend(
  filters?: Array<{ key: string; operator: 'eq' | 'neq'; value: string }> | null
): SpendMetadataFilter[] | null {
  if (!filters || filters.length === 0) return null;
  
  return filters.map((f) => ({
    key: f.key,
    operation: f.operator === 'eq' ? 'is' as const : 'is_not' as const,
    value: f.value,
  }));
}

export function useOverviewSpend(
  dateRange?: DateRange,
  metadataFilters?: Array<{ key: string; operator: 'eq' | 'neq'; value: string }> | null
) {
  // Use provided date range or fallback to default
  const effectiveDateRange = dateRange || DEFAULT_DATE_RANGE;

  // Use the same date range as the lastXDays graph
  const buildSpendRequest = React.useCallback((): SpendRequest | null => {
    if (!effectiveDateRange?.from || !effectiveDateRange?.to) return null;

    const startDate = new Date(effectiveDateRange.from);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(effectiveDateRange.to);
    endDate.setHours(23, 59, 59, 999);

    const request: SpendRequest = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      providers: null, // Include all providers for overview
      model_selection: ALL_MODELS_SELECTION, // Include all models for overview
      metadata_filters: mapMetadataFiltersForSpend(metadataFilters),
    };

    return request;
  }, [effectiveDateRange, metadataFilters]);

  const spendRequest = buildSpendRequest();

  const { data: spendData, isLoading: isLoadingSpend } = useSpend(
    spendRequest,
    spendRequest !== null
  );

  return {
    spendData,
    isLoadingSpend,
  };
}
